import { createFeatureSelector, createSelector } from '@ngrx/store';
import { SDDTransactionTypeEnum } from 'src/app/enums/sdd-transaction-type.enum';
import { Deposit } from 'src/app/models/deposit.model';
import { DepositDetails } from 'src/app/models/deposits-details.model';
import { EBillsServiceModel } from 'src/app/models/e-bills-service.model';
import { eBillsModel } from 'src/app/models/e-bills.model';
import { Loan } from 'src/app/models/loan.model';
import { Saving } from 'src/app/models/saving.model';
import { SDDLimitDetails } from 'src/app/models/sdd-limit-details.model';
import { SDDTransactionDetails } from 'src/app/models/sdd-transaction-details.model';
import { Currency } from '../../../enums/currency.enum';
import { AccountDetails } from '../../../models/account-details.model';
import { Statement } from '../../../models/statement.model';
import * as fromAccounts from '../../reducers/accounts/accounts.reducer';
import * as fromCards from '../../reducers/cards/cards.reducer';
import * as fromDashboard from '../../reducers/dashboard/dashboard.reducer';
import { PaymentStatusEnum } from './../../../enums/payment-status.enum';
import { AccountTransactionFilter } from './../../../models/account-transaction-filter.model';
import { Account } from './../../../models/account.model';
import { BankCurrency } from './../../../models/bank-currency.model';
import { Country } from './../../../models/country.model';
import { SavingsDetails } from './../../../models/saving-details.model';
import { Transaction } from './../../../models/transaction.model';
import { EBillsRelationshipModel } from 'src/app/models/e-bills-relationship.model';

export const selectDashboardStateFeature =
  createFeatureSelector<fromDashboard.State>(fromDashboard.dashboardFeatureKey);

export const selectAccountStateFeature =
  createFeatureSelector<fromAccounts.State>(fromAccounts.accountFeatureKey);

export const selectCardStateFeature = createFeatureSelector<fromCards.State>(
  fromCards.cardsFeatureKey
);

export interface AccountsViewModel {
  accounts: Account[] | null;
  selectedAccount: Account | null;
}
export interface LoansViewModel {
  loans: Loan[] | null;
  selectedLoan: Loan | null;
}

export interface SavingsViewModel {
  savings: Saving[] | null;
  selectedSaving: Saving | null;
}
export interface PrepaidViewModel {
  prepaidAccounts: Account[] | null;
  selectedPrepaid: Account | null;
}
export interface DepositsViewModel {
  deposits: Deposit[] | null;
  selectedDeposit: Deposit | null;
}

export interface PrepaidIbanViewModel {
  prepaidIban: any | null;
}
export interface LoanDetailsViewModel {
  selectedLoan: Loan | null;
  accountDetails: AccountDetails | null;
}
export interface SavingDetailsViewModel {
  selectedSaving: Saving | null;
  savingDetails: SavingsDetails | null;
}
export interface DepositDetailsViewModel {
  selectedDeposit: Deposit | null;
  depositDetails: DepositDetails | null;
}
export interface AccountDetailsViewModel {
  selectedAccount: Account | null;
  accountDetails: AccountDetails | null;
}

export interface AccountIdModel {
  accountId: string | undefined;
}

export interface AccountDetailsParamsModel {
  accountId: string | null;
  currency: Currency | undefined | null;
}

export interface SavingDetailsParamsModel {
  savingsId: string | null;
}
export interface DepositDetailsParamsModel {
  depositId: string | null;
}
export interface LoanDetailsParamsModel {
  accountId: string | null;
}
export interface TransactionHistoryParamsModel {
  iban: string | undefined;
  currency: Currency | undefined | null;
  accountId?: string | undefined | null;
  callCreditor: string | undefined;
  callDebtor: string | undefined;
  cards: boolean | undefined;
  description: string | undefined;
  modelCreditor: string | undefined;
  modelDebtor: string | undefined;
  office: boolean | undefined;
  online: boolean | undefined;
  payerPayee: string | undefined;
  paymentAmountFrom: string | undefined;
  paymentAmountTo: string | undefined;
  paymentDateFrom: string | undefined;
  paymentDateTo: string | undefined;
  transactionStatus: string | undefined;
  debtorReferenceType?: string | undefined;
  creditorReferenceType?: string | undefined;
  transactionFlow?: string | undefined;
  accountType?: string | undefined;
}
export interface eBillsParamsModel {
  iban: string | undefined;
  page: number | undefined;
  pageSize: number | undefined;
  paymentStatus: string | undefined | null;
  receivedSentEnum: string | undefined | null;
}

export interface sddParamsModel {
  payerAccount: string | undefined;
  sddTransactionType: string | undefined | null;
  paymentDateFrom: string | undefined | null;
  paymentDateTo: string | undefined | null;
}

export interface sddDetailsModel {
  sddTransaction: SDDTransactionDetails | null;
  selectedAccount: Account | null;
}
export interface sddLimitsDetailsModel {
  sddLimits: SDDLimitDetails | null;
  selectedAccount: Account | null;
}
export interface eBillsFilterServicesParamsModel {
  creditorAccount: string | undefined;
  creditorName: string | undefined;
  creditorTaxNumber: string | undefined;
  page: number | undefined;
  pageSize: number | undefined;
}
export interface eBillsFilterSubscribedParamsModel {
  directPayment?: string;
  sortByDate?: boolean;
  sortDesc?: boolean;
  creditorAccount?: string | undefined;
  debtorAccount?: string | undefined;
  page: number | undefined;
  pageSize: number | undefined;
}
export interface eBillDetailsModel {
  invoice: eBillsModel | null;
  selectedAccount: Account | null;
}

export interface eBillServiceDetailsModel {
  service: EBillsServiceModel | null;
  selectedAccount: Account | null;
}
export interface SelectedUnsignedOrderModel {
  selectedUnsignedOrder: Transaction | null;
  selectedAccount: Account | null;
}

export interface AccTransactionHistoryModel {
  selectedAccount: Account | null;
  transactionHistory: Transaction[] | null;
  filtersApplied: number;
}

export interface SavingsTransactionHistoryModel {
  selectedSavings: Saving | null;
  transactionHistory: Transaction[] | null;
  filtersApplied: number;
}
export interface AccAnnouncementHistoryModel {
  selectedAccount: Account | null;
  announcementHistory: Transaction[] | null;
  filtersApplied: number;
}

export interface SddListModel {
  selectedAccount: Account | null;
  sddList: SDDTransactionDetails[] | null;
}
export interface SddLimitsModel {
  selectedAccount: Account | null;
  sddLimits: SDDLimitDetails[] | null;
}
export interface AccEbillsModel {
  selectedAccount: Account | null;
  ebills: eBillsModel[] | null;
  filtersApplied: number;
}
export interface AccEbillsServicesListModel {
  selectedAccount: Account | null;
  eBillsServicesList: EBillsServiceModel[] | null;
  filtersApplied: number;
}
export interface EBillsSubscribedListModel {
  selectedAccount: Account | null;
  eBillsSubscribedList: EBillsRelationshipModel[] | null;
  filtersApplied: number;
}
export interface UnsignedOrdersParamsModel {
  iban: string | null;
  currency: Currency | undefined | null;
  accountId: string | null;
}
export interface UnsignedOrdersSelectedIdModel {
  selectedOrders: Transaction[] | null;
}

export interface AccUnsignedOrderModel {
  selectedAccount: Account | null;
  unsignedOrders: Transaction[] | null;
}

export interface AccStatementsModel {
  selectedAccount: Account | null;
  statements: Statement[] | null;
}

export interface AccountUnsignedOrdersFiltersModel {
  selectedAccount: Account | null;
  unsignedOrdersFilters: AccountTransactionFilter | null;
}

export interface AccountTransactionDetailsModel {
  accountTransactionDetails: Transaction | null;
  selectedAccount: Account | null;
}
export interface SavingsTransactionDetailsModel {
  savingsTransactionDetails: Transaction | null;
  selectedSaving: Saving | null;
}

export interface PopulateTransactionDetailsFormModel {
  transactionDetailsForm: Transaction | null;
}

export interface AccountTransactionsFiltersModel {
  transactionsFilters: AccountTransactionFilter | null;
  transactionsRetailFilters: AccountTransactionFilter | null;
  selectedAccount: Account | null;
}

export interface SavingsTransactionsFiltersModel {
  savingsTransactionsFilters: AccountTransactionFilter | null;
  selectedSaving: Saving | null;
}
export interface AccountAnnouncementsFiltersModel {
  announcementsFilters: AccountTransactionFilter | null;
  selectedAccount: Account | null;
}

export interface AccountStatementsFiltersModel {
  statementsFilters: AccountTransactionFilter | null;
  selectedAccount: Account | null;
}

export interface ModelNumberModel {
  modelNumbers: string[] | undefined;
}
export interface ReferenceModel {
  references: string[] | undefined;
}
export interface CountryModel {
  countries: Country[] | undefined;
}
export interface BankCurrencyModel {
  bankCurrencies: BankCurrency[] | undefined;
}

export interface SDDParamsModel {
  payerAccount: string | undefined;
  sddTransactionType: string | undefined | null;
}

// SELECTORS
export const selectAccountsViewModel = createSelector(
  selectDashboardStateFeature,
  selectAccountStateFeature,
  (
    dashboardState: fromDashboard.State,
    accountsState: fromAccounts.State
  ): AccountsViewModel => {
    const accountsArray = dashboardState.dashboard?.accounts?.map(account => {
      if (account.accountId === accountsState.selectedAccount?.accountId) {
        return {
          ...account,
          activeBalance: accountsState.selectedAccount.activeBalance
        };
      }
      return account;
    });
    return {
      accounts: accountsArray ? accountsArray : null,
      selectedAccount:
        accountsState.selectedAccount?.accountType == 'CURRENT_ACCOUNT' ||
        accountsState.selectedAccount?.accountType == 'CURRENT_PROXY_ACCOUNT'
          ? accountsState.selectedAccount
          : dashboardState.dashboard && dashboardState.dashboard.accounts
          ? dashboardState.dashboard.accounts[0]
          : null
    };
  }
);
export const selectLoansViewModel = createSelector(
  selectDashboardStateFeature,
  selectAccountStateFeature,
  (
    dashboardState: fromDashboard.State,
    accountsState: fromAccounts.State
  ): LoansViewModel => {
    const loansArray = dashboardState.dashboard?.loans?.map(loan => {
      if (loan.accountId === accountsState.selectedLoan?.accountId) {
        return {
          ...loan,
          activeBalance: accountsState.selectedLoan.activeBalance
        };
      }
      return loan;
    });
    return {
      loans: loansArray ? loansArray : null,
      selectedLoan: accountsState.selectedLoan
    };
  }
);

export const selectSavingsViewModel = createSelector(
  selectDashboardStateFeature,
  selectAccountStateFeature,
  (
    dashboardState: fromDashboard.State,
    accountsState: fromAccounts.State
  ): SavingsViewModel => {
    let savingsArray: any = [];
    dashboardState.dashboard?.savings?.forEach(saving => {
      if (saving.accountId === accountsState.selectedSaving?.accountId) {
        savingsArray.push({
          ...saving,
          activeBalance: accountsState.selectedSaving.activeBalance
        });
      } else savingsArray.push(saving);
    });
    dashboardState.dashboard?.gradualSavings?.forEach(saving => {
      if (saving.accountId === accountsState.selectedSaving?.accountId) {
        savingsArray.push({
          ...saving,
          activeBalance: accountsState.selectedSaving.activeBalance
        });
      } else savingsArray.push(saving);
    });

    return {
      savings: savingsArray ? savingsArray : null,
      selectedSaving: accountsState.selectedSaving
    };
  }
);

export const selectPrepaidViewModel = createSelector(
  selectDashboardStateFeature,
  selectAccountStateFeature,
  (
    dashboardState: fromDashboard.State,
    accountsState: fromAccounts.State
  ): PrepaidViewModel => {
    return {
      prepaidAccounts: dashboardState.dashboard
        ? dashboardState.dashboard.prepaidAccounts
        : null,
      selectedPrepaid: accountsState.selectedPrepaid
    };
  }
);

export const selectDepositsViewModel = createSelector(
  selectDashboardStateFeature,
  selectAccountStateFeature,
  (
    dashboardState: fromDashboard.State,
    accountsState: fromAccounts.State
  ): DepositsViewModel => {
    const depositsArray = dashboardState.dashboard?.deposits?.map(deposit => {
      if (deposit.accountId === accountsState.selectedDeposit?.accountId) {
        return {
          ...deposit,
          activeBalance: accountsState.selectedDeposit.activeBalance
        };
      }
      return deposit;
    });
    return {
      deposits: depositsArray ? depositsArray : null,
      selectedDeposit: accountsState.selectedDeposit
    };
  }
);

export const selectAccountDetailsViewModel = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountDetailsViewModel => {
    return {
      selectedAccount: state.selectedAccount,
      accountDetails: state.accountDetails
    };
  }
);
export const selectSavingDetailsViewModel = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SavingDetailsViewModel => {
    return {
      selectedSaving: state.selectedSaving,
      savingDetails: state.savingDetails
    };
  }
);
export const selectDepositDetailsViewModel = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): DepositDetailsViewModel => {
    return {
      selectedDeposit: state.selectedDeposit,
      depositDetails: state.depositDetails
    };
  }
);
export const selectLoanDetailsViewModel = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): LoanDetailsViewModel => {
    return {
      selectedLoan: state.selectedLoan,
      accountDetails: state.accountDetails
    };
  }
);

export const selectAccountId = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountIdModel => {
    return {
      accountId: state.selectedAccount?.accountId
    };
  }
);

export const selectAccDetailsParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountDetailsParamsModel => {
    return {
      accountId: state.selectedAccount ? state.selectedAccount.accountId : null,
      currency: state.selectedAccount
        ? state.selectedAccount.activeBalance?.currency
        : null
    };
  }
);

export const selectSavingDetailsParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SavingDetailsParamsModel => {
    return {
      savingsId: state.selectedSaving ? state.selectedSaving.accountId : null
    };
  }
);

export const selectDepositDetailsParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): DepositDetailsParamsModel => {
    return {
      depositId: state.selectedDeposit ? state.selectedDeposit.accountId : null
    };
  }
);

export const selectLoanDetailsParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): LoanDetailsParamsModel => {
    return {
      accountId: state.selectedLoan ? state.selectedLoan.accountId : null
    };
  }
);

export const selectSavingsTransactionHistoryParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): TransactionHistoryParamsModel => {
    return {
      iban: state.selectedSaving?.accountIban,
      currency: state.selectedSaving
        ? state.selectedSaving.balance[0]?.currency
        : null,
      accountId: state.selectedSaving?.accountId,
      accountType: state.selectedSaving?.accountType,
      callCreditor: state.savingsTransactionsFilters?.callCreditor,
      callDebtor: state.savingsTransactionsFilters?.callDebtor,
      cards: state.savingsTransactionsFilters?.cards,
      transactionFlow: state.savingsTransactionsFilters?.transactionFlow,
      description: state.savingsTransactionsFilters?.description,
      modelCreditor: state.savingsTransactionsFilters?.modelCreditor,
      modelDebtor: state.savingsTransactionsFilters?.modelDebtor,
      office: state.savingsTransactionsFilters?.office,
      online: state.savingsTransactionsFilters?.online,
      payerPayee: state.savingsTransactionsFilters?.payerPayee,
      paymentAmountFrom: state.savingsTransactionsFilters?.paymentAmountFrom,
      paymentAmountTo: state.savingsTransactionsFilters?.paymentAmountTo,
      paymentDateFrom: state.savingsTransactionsFilters?.paymentDateFrom,
      paymentDateTo: state.savingsTransactionsFilters?.paymentDateTo,
      transactionStatus: state.savingsTransactionsFilters?.transactionStatus,
      creditorReferenceType:
        state.savingsTransactionsFilters?.creditorReferenceType,
      debtorReferenceType: state.savingsTransactionsFilters?.debtorReferenceType
    };
  }
);

export const selectAccTransactionHistoryParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): TransactionHistoryParamsModel => {
    return {
      iban: state.selectedAccount?.accountIban,
      currency: state.selectedAccount
        ? state.selectedAccount.activeBalance?.currency
        : null,
      accountId: state.selectedAccount?.accountId,
      accountType: state.selectedAccount?.accountType,
      callCreditor: state.accountTransactionsFilters?.callCreditor,
      callDebtor: state.accountTransactionsFilters?.callDebtor,
      cards: state.accountTransactionsFilters?.cards,
      transactionFlow: state.accountTransactionsFilters?.transactionFlow,
      description: state.accountTransactionsFilters?.description,
      modelCreditor: state.accountTransactionsFilters?.modelCreditor,
      modelDebtor: state.accountTransactionsFilters?.modelDebtor,
      office: state.accountTransactionsFilters?.office,
      online: state.accountTransactionsFilters?.online,
      payerPayee: state.accountTransactionsFilters?.payerPayee,
      paymentAmountFrom: state.accountTransactionsFilters?.paymentAmountFrom,
      paymentAmountTo: state.accountTransactionsFilters?.paymentAmountTo,
      paymentDateFrom: state.accountTransactionsFilters?.paymentDateFrom,
      paymentDateTo: state.accountTransactionsFilters?.paymentDateTo,
      transactionStatus: state.accountTransactionsFilters?.transactionStatus,
      creditorReferenceType:
        state.accountTransactionsFilters?.creditorReferenceType,
      debtorReferenceType: state.accountTransactionsFilters?.debtorReferenceType
    };
  }
);

export const selectAccAnnouncementHistoryParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): TransactionHistoryParamsModel => {
    return {
      iban: state.selectedAccount?.accountIban,
      currency: state.selectedAccount
        ? state.selectedAccount.activeBalance?.currency
        : null,
      accountId: state.selectedAccount?.accountId,
      accountType: state.selectedAccount?.accountType,
      callCreditor: state.accountAnnouncementsFilters?.callCreditor,
      callDebtor: state.accountAnnouncementsFilters?.callDebtor,
      cards: state.accountAnnouncementsFilters?.cards,
      transactionFlow: state.accountAnnouncementsFilters?.transactionFlow,
      description: state.accountAnnouncementsFilters?.description,
      modelCreditor: state.accountAnnouncementsFilters?.modelCreditor,
      modelDebtor: state.accountAnnouncementsFilters?.modelDebtor,
      office: state.accountAnnouncementsFilters?.office,
      online: state.accountAnnouncementsFilters?.online,
      payerPayee: state.accountAnnouncementsFilters?.payerPayee,
      paymentAmountFrom: state.accountAnnouncementsFilters?.paymentAmountFrom,
      paymentAmountTo: state.accountAnnouncementsFilters?.paymentAmountTo,
      paymentDateFrom: state.accountAnnouncementsFilters?.paymentDateFrom,
      paymentDateTo: state.accountAnnouncementsFilters?.paymentDateTo,
      transactionStatus: state.accountAnnouncementsFilters?.transactionStatus,
      creditorReferenceType:
        state.accountAnnouncementsFilters?.creditorReferenceType,
      debtorReferenceType:
        state.accountAnnouncementsFilters?.debtorReferenceType
    };
  }
);

export const selectEBillsListParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): eBillsParamsModel => {
    const maxInteger = Math.pow(2, 31) - 1;
    return {
      iban: state.selectedAccount?.accountIban,
      page: 1,
      pageSize: maxInteger,
      paymentStatus: state.accountFilterEBills
        ? state.accountFilterEBills
        : PaymentStatusEnum.NONE,
      receivedSentEnum: state.receivedSentEnum
    };
  }
);

export const selectSddListParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): sddParamsModel => {
    return {
      payerAccount: state.selectedAccount?.accountIban,
      sddTransactionType: state.sddTransactionType
        ? state.sddTransactionType
        : SDDTransactionTypeEnum.ACTIVE_SDD,
      paymentDateFrom: '',
      paymentDateTo: ''
    };
  }
);

export const selectSddList = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SddListModel => {
    return {
      selectedAccount: state.selectedAccount,
      sddList: state.sddList
    };
  }
);
export const selectSddLimits = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SddLimitsModel => {
    return {
      selectedAccount: state.selectedAccount,
      sddLimits: state.sddLimits
    };
  }
);

export const selectSddDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): sddDetailsModel => {
    return {
      sddTransaction: state.sddDetails,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectSddLimitsDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): sddLimitsDetailsModel => {
    return {
      sddLimits: state.sddLimitsDetails,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectEBillsList = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccEbillsModel => {
    return {
      selectedAccount: state.selectedAccount,
      ebills: state.eBillsList,
      filtersApplied: state.filtersApplied
    };
  }
);

export const selectEBillDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): eBillDetailsModel => {
    return {
      invoice: state.eBillsDetails,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectEBillsFileDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): any => {
    return {
      eBillsFileDetails: state.eBillsFileDetails,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectEBillServiceDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): eBillServiceDetailsModel => {
    return {
      service: state.eBillsServiceDetails,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectEBillsServicesListParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): eBillsFilterServicesParamsModel => {
    const maxInteger = Math.pow(2, 31) - 1;
    return {
      creditorAccount: state.eBillsServicesFilters?.creditorAccount,
      creditorName: state.eBillsServicesFilters?.creditorName,
      creditorTaxNumber: state.eBillsServicesFilters?.creditorTaxNumber,
      page: 1,
      pageSize: maxInteger
    };
  }
);

export const selectEBillsSubscribedListParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): eBillsFilterSubscribedParamsModel => {
    const maxInteger = Math.pow(2, 31) - 1;
    return {
      debtorAccount: state.eBillsSubscribedFilters?.debtorAccount,
      creditorAccount: state.eBillsSubscribedFilters?.creditorAccount,
      directPayment: state.eBillsSubscribedFilters?.directPayment,
      sortByDate: state.eBillsSubscribedFilters?.sortByDate,
      sortDesc: state.eBillsSubscribedFilters?.sortDesc,
      page: 1,
      pageSize: maxInteger
    };
  }
);

export const selectEBillsServicesList = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccEbillsServicesListModel => {
    return {
      selectedAccount: state.selectedAccount,
      eBillsServicesList: state.eBillsServicesList,
      filtersApplied: state.filtersApplied
    };
  }
);
export const selectEBillsSubscribedList = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): EBillsSubscribedListModel => {
    return {
      selectedAccount: state.selectedAccount,
      eBillsSubscribedList: state.eBillsSubscribedList,
      filtersApplied: state.filtersApplied
    };
  }
);

export const selectAccTransactionHistory = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccTransactionHistoryModel => {
    return {
      selectedAccount: state.selectedAccount,
      transactionHistory: state.accountTransactions,
      filtersApplied: state.filtersApplied
    };
  }
);

export const selectSavingsTransactionHistory = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SavingsTransactionHistoryModel => {
    return {
      selectedSavings: state.selectedSaving,
      transactionHistory: state.savingTransactions,
      filtersApplied: state.filtersApplied
    };
  }
);

export const selectAccAnnouncementHistory = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccAnnouncementHistoryModel => {
    return {
      selectedAccount: state.selectedAccount,
      announcementHistory: state.accountAnnouncements,
      filtersApplied: state.filtersApplied
    };
  }
);

export const selectAccUnsignedOrdersParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): UnsignedOrdersParamsModel => {
    return {
      iban: state.selectedAccount ? state.selectedAccount?.accountIban : null,
      currency: state.selectedAccount ? state.selectedAccount?.currency : null,
      accountId: state.selectedAccount ? state.selectedAccount?.accountId : null
    };
  }
);

export const selectAccStatements = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccStatementsModel => {
    return {
      selectedAccount: state.selectedAccount,
      statements: state.statements
    };
  }
);

export const selectAccUnsignedOrders = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccUnsignedOrderModel => {
    return {
      selectedAccount: state.selectedAccount,
      unsignedOrders: state.unsignedOrders
    };
  }
);

export const selectActiveAccount = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountStatementsFiltersModel => {
    return {
      selectedAccount: state.selectedAccount,
      statementsFilters: state.accountStatementsFilters
    };
  }
);

export const selectUnsignedOrdersByIds = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): UnsignedOrdersSelectedIdModel => {
    return {
      selectedOrders: state.selectedOrder ? state.selectedOrder : null
    };
  }
);

export const selectAccountTransactionDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountTransactionDetailsModel => {
    return {
      accountTransactionDetails: state.accountTransactionDetails,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectSavingsTransactionDetails = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SavingsTransactionDetailsModel => {
    return {
      savingsTransactionDetails: state.savingsTransactionDetails,
      selectedSaving: state.selectedSaving
    };
  }
);

export const selectAccountUnsignedOrdersFilters = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountUnsignedOrdersFiltersModel => {
    return {
      selectedAccount: state.selectedAccount,
      unsignedOrdersFilters: state.accountUnsignedOrdersFilters
    };
  }
);

export const getSelectedUnsignedOrder = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State, props: any): SelectedUnsignedOrderModel => {
    let unsignedOrderDetails = null;
    if (state.unsignedOrders) {
      unsignedOrderDetails = state.unsignedOrders.find(
        order => order.transactionId == props.id
      );
    }
    return {
      selectedUnsignedOrder: unsignedOrderDetails ? unsignedOrderDetails : null,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectAccountTransactionsFilters = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountTransactionsFiltersModel => {
    return {
      transactionsFilters: state.accountTransactionsFilters,
      transactionsRetailFilters: state.accountRetailTransactionsFilters,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectSavingTransactionsFilters = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SavingsTransactionsFiltersModel => {
    return {
      savingsTransactionsFilters: state.savingsTransactionsFilters,
      selectedSaving: state.selectedSaving
    };
  }
);
export const selectAccountStatementsFilters = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountStatementsFiltersModel => {
    return {
      statementsFilters: state.accountStatementsFilters,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectAccountAnnouncementsFilters = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): AccountAnnouncementsFiltersModel => {
    return {
      announcementsFilters: state.accountAnnouncementsFilters,
      selectedAccount: state.selectedAccount
    };
  }
);

export const selectModelNumbers = createSelector(
  selectDashboardStateFeature,
  (state: fromDashboard.State): ModelNumberModel => {
    return {
      modelNumbers: state.dashboard?.modelNumbers
    };
  }
);

export const selectReference = createSelector(
  selectDashboardStateFeature,
  (state: fromDashboard.State): ReferenceModel => {
    return {
      references: state.dashboard?.references
    };
  }
);

export const selectCountry = createSelector(
  selectDashboardStateFeature,
  (state: fromDashboard.State): CountryModel => {
    return {
      countries: state.dashboard?.countries
    };
  }
);

export const selectBankCurrencies = createSelector(
  selectDashboardStateFeature,
  (state: fromDashboard.State): BankCurrencyModel => {
    return {
      bankCurrencies: state.dashboard?.bankCurrencies
    };
  }
);

export const selectPrepaidIbanInternalPayment = createSelector(
  selectAccountStateFeature,
  (accountsState: fromAccounts.State): PrepaidIbanViewModel => {
    return {
      prepaidIban: accountsState.setPrepaidIban
    };
  }
);

export const selectSDDListParams = createSelector(
  selectAccountStateFeature,
  (state: fromAccounts.State): SDDParamsModel => {
    return {
      payerAccount: state.selectedAccount?.accountIban,
      sddTransactionType: state.sddTransactionType
        ? state.sddTransactionType
        : SDDTransactionTypeEnum.ACTIVE_SDD
    };
  }
);
